import { ChevronDown, ChevronUp } from 'react-feather';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetEventFundraisingStatsQuery as GetEventFundraisingStats, Permission } from '__generated__/graphql';

import {
  FilterIndicator, StatsListContainer, StatsListFooter, StatsListHeader, StatsListRow, StickyStatsListCard,
  StickyStatsListHeader,
} from './Layout';
import { assetUrl } from '../../common/helpers';
import ProtectedSection from '../Common/ProtectedSection';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';
import useProject from '../../common/useProject';

type Event = GetEventFundraisingStats['event'];
type CharityData = Event['analytics']['fundraising'][0];

export interface FundraisingStatsProps {
  event: Event;
  limit?: number;
}

const FundraisingStats = memo(({ event, limit = 5 } : FundraisingStatsProps) => {
  const { t } = useTranslation();
  const { formatInteger, formatCurrency } = useLocale();
  const project = useProject();
  const device = useMediaDevice();

  const [showingAll, setShowingAll] = useState(false);
  const rows = event.analytics.fundraising;
  const fundsRaised = rows.reduce((sum: number, data: CharityData) => sum + data.funds_raised, 0);
  const campaignsCount = rows.reduce((sum: number, data: CharityData) => sum + data.campaigns_count, 0);
  const visibleRows = showingAll ? rows : rows.slice(0, limit);

  return (
    <StickyStatsListCard id="FundraisingStatsContainer">
      <StickyStatsListHeader>
        <StatsListHeader>
          <UI.FlexContainer sc={{ alignItems: 'center' }}>
            <UI.H4>
              {t('fundraisers')}
            </UI.H4>
            <FilterIndicator sc={{ ml: 2 }} />
          </UI.FlexContainer>
          <UI.Div />
          {rows.length > 0 && (
            <>
              <UI.Legend sc={{ small: true }}>
                {t('pages')}
              </UI.Legend>
              <UI.Legend sc={{ small: true }}>
                {t('funds_raised')}
              </UI.Legend>
            </>
          )}
        </StatsListHeader>
      </StickyStatsListHeader>
      <StatsListContainer>
        {rows.length === 0 && (
          <UI.Div sc={{ padding: 4 }}>
            <UI.FlexContainer sc={{ justifyContent: 'center', mb: 4 }}>
              <img src={assetUrl('images/dashboard-tickets.svg')} height={100} alt={t('fundraising')} />
            </UI.FlexContainer>
            <UI.Div sc={{ textAlign: 'center', color: 'gray.400' }}>
              {t('no_fundraisers')}
              <ProtectedSection permissions={[Permission.edit_events]}>
                {' '}
                <UI.Link to={`/${project.id}/events/${event.id}/fundraising`}>
                  {t('manage_fundraising_settings')}
                </UI.Link>
              </ProtectedSection>
            </UI.Div>
          </UI.Div>
        )}
        {visibleRows.map((data) => (
          <CharityRow data={data} key={data.charity.id} />
        ))}
        {(showingAll || rows.length <= limit) && rows.length > 1 && (
          <StatsListRow>
            <UI.Div sc={{ fontWeight: 500 }}>
              {t('totals')}
            </UI.Div>
            {!device.isSmall && <UI.Div />}
            <UI.Div sc={{ noWrap: true }}>
              {formatInteger(campaignsCount)}
            </UI.Div>
            {!device.isSmall && (
              <UI.Div sc={{ noWrap: true, muted: true }}>
                {formatCurrency(fundsRaised)}
              </UI.Div>
            )}
          </StatsListRow>
        )}
      </StatsListContainer>
      {rows.length > limit && (
        <StatsListFooter>
          {!showingAll && (
            <UI.Button
              onClick={() => setShowingAll(true)}
              sc={{ blank: true, size: 'xs', mr: 2 }}
            >
              {t('show_all')}
              {' '}
              <UI.Span sc={{ muted: true }}>
                {`(${rows.length})`}
              </UI.Span>
              {' '}
              <UI.Icon sc={{ muted: true }}>
                <ChevronDown />
              </UI.Icon>
            </UI.Button>
          )}
          {showingAll && (
            <UI.Button
              onClick={() => setShowingAll(false)}
              sc={{ blank: true, size: 'xs', mr: 2 }}
            >
              {t('show_less')}
              {' '}
              <UI.Icon sc={{ muted: true }}>
                <ChevronUp />
              </UI.Icon>
            </UI.Button>
          )}
        </StatsListFooter>
      )}
    </StickyStatsListCard>
  );
});

interface CharityRowProps {
  data: CharityData;
}

const CharityRow = ({ data }: CharityRowProps) => {
  const { formatInteger, formatCurrency } = useLocale();
  const device = useMediaDevice();

  const { charity } = data;

  return (
    <StatsListRow>
      <UI.Div style={{ minWidth: 0 }}>
        <UI.FlexContainer sc={{ alignItems: 'center' }}>
          {charity.logo_url && (
            <UI.Div style={{ width: 18, height: 14 }} sc={{ mr: 1 }}>
              <img
                src={charity.logo_url}
                alt={charity.title}
                style={{ display: 'block', objectFit: 'contain', width: 18, height: 14 }}
              />
            </UI.Div>
          )}
          <UI.Div>
            <UI.Truncate>
              {charity.title}
            </UI.Truncate>
          </UI.Div>
        </UI.FlexContainer>
      </UI.Div>
      {!device.isSmall && <UI.Div />}
      <UI.Div sc={{ noWrap: true }}>
        {formatInteger(data.campaigns_count)}
      </UI.Div>
      {!device.isSmall && (
        <UI.Div sc={{ noWrap: true, muted: true }}>
          {formatCurrency(data.funds_raised)}
        </UI.Div>
      )}
    </StatsListRow>
  );
};

export default FundraisingStats;
